import Controller from '@ember/controller';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed, set } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/combo-select-input';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import {
  RI_BREAKDOWNS,
  type RiBreakdown,
} from 'frontend-utils/utils/constants/constant-values';
import type { SupportedCurrencies } from 'frontend-utils/utils/constants/currency-mapping-constants';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import cloneDeep from 'lodash.clonedeep';
import type { SegmentationBlock } from 'lumen/components/hotel-and-market/revenue-insight/sort-and-reorder-blocks';
import type { SegmentationValueLike } from 'lumen/models/revenue-insight/segmentation-value';
import type MarketRevenueInsightRoute from 'lumen/routes/market/revenue-insight';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import type { DateAggregationType } from 'lumen/utils/constants/general-constants';
import {
  KPI_DISPLAY_CUSTOM_MAPPING,
  MAPPING_TYPE_RATE_CODE,
} from 'lumen/utils/constants/pms-mapping-constants';
import {
  ALL_SELECTABLE_SEGMENTATION_BLOCKS,
  CANCELLATION_ONLY_BLOCKS,
  DEFAULT_SEGMENTATION_BLOCKS_ORDER,
  FILTERABLE_SEGMENTATION_BLOCKS,
  type FilterableSegmentationBlock,
  HOTEL_SORT_BY,
  type HotelSortBy,
  type KpiCategory,
  KPIS,
  MAX_AMOUNT_OF_BLOCKS,
  MAX_AMOUNT_OF_FILTERS_PER_BLOCK,
  TARGET_BLOCKS,
  type ValidKpi,
  WEEKS_IN_YEAR,
} from 'lumen/utils/constants/ri-constant-values';
import {
  type ActiveFilter,
  addFilter,
  addFiltersToActiveFilters,
  getParents,
  getSegmentationValue,
  hasAllSelected,
  hasFilter,
  mergeFilters,
  removeFilter,
  removeFilters,
  toggleFilter,
} from 'lumen/utils/filter-bar/filter-functions';
import { parseObject, stringifyObject } from 'lumen/utils/parse-utils';
import { formatUrl } from 'lumen/utils/rates-request';
import {
  createSegmentationBlockStructure,
  getSdlyEnd,
  type SegmentationBlockStructure,
  sortValues,
} from 'lumen/utils/revenue-insight/controller-utils';
import getDateRange from 'lumen/utils/revenue-insight/get-date-range';
import {
  segmentationIdFormatter,
  segmentationValueFormatter,
} from 'lumen/utils/revenue-insight/segmentation-value-formatter';
import type { ModelFrom } from 'lumen/utils/routes';
import { timeout } from 'lumen/utils/waiters';

// In some breakdowns we show different KPIs (e.g. pickup, pace and tooltips). These are the KPIs to replace.
const KPIS_TO_REPLACE = {
  [KPIS.OCCUPANCY]: KPIS.NR_OF_ROOMS,
  [KPIS.REVPAR]: KPIS.REVENUE,
};

const DEFAULT_BREAKDOWNS = [
  RI_BREAKDOWNS.ALL,
  RI_BREAKDOWNS.IMPACT_DATE,
  RI_BREAKDOWNS.PACE,
  RI_BREAKDOWNS.PICKUP,
];

type ParsedFilters = Record<string, string[]>;

export default class MarketRevenueInsightController extends Controller {
  // @ts-expect-error: Incorrect type
  declare model: ModelFrom<MarketRevenueInsightRoute>;

  // @ts-expect-error: Property 'queryParams' in type 'MarketRevenueInsightController' is not assignable to the same property in base type 'Controller<unknown>'.
  queryParams = [
    {
      kpi: { scope: 'controller' },
      kpiCategory: { scope: 'controller' },
      roomsTab: { scope: 'controller' },
      revenueTab: { scope: 'controller' },
      // the actual segmentation value filters
      segmentationFilters: { scope: 'controller' },
      stayDateFilter: { scope: 'controller' },
      showFullPortfolio: { scope: 'controller' },
      sortHotelsBy: { scope: 'controller' },
      sortHotelsAscending: { scope: 'controller' },
      // Date related parameters
      fromDate: { scope: 'controller' },
      toDate: { scope: 'controller' },
      refFromDate: { scope: 'controller' },
      reference: { scope: 'controller' },
      extractDate: { scope: 'controller' },
      stayDateAggregation: { scope: 'controller' },
      hidePickupLY: { scope: 'controller' },
      currency: { scope: 'controller' },
    },
  ] as const;

  @service declare ajax: Services['ajax'];
  @service declare alerting: Services['alerting'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service declare riSelections: Services['ri-selections'];
  @service declare router: Services['router'];
  @service declare toast: Services['toast'];

  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked activeFilters: ActiveFilter[] = [];
  @tracked currency?: SupportedCurrencies;
  @tracked defaultSegmentationBlocksOrder: SegmentationBlock[] =
    DEFAULT_SEGMENTATION_BLOCKS_ORDER;
  @tracked extractDate: string | null = null;
  @tracked fromDate: string | null = null;
  @tracked hidePickupLY = false;
  @tracked isFilterBarHovered: boolean = false;
  // Is true by default (this means it is the first visit), will be set to false after the needed checks in the beforeModel hook
  @tracked isInitialVisit = true;
  @tracked isShowingSelectionAndSortModal = false;
  @tracked kpi: ValidKpi = DEFAULT_QP_VALUES.hotel['revenue-insight'].kpi;
  @tracked kpiCategory: KpiCategory =
    DEFAULT_QP_VALUES.hotel['revenue-insight'].kpiCategory;
  @tracked reference = 'SWLY';
  @tracked refFromDate: string | null = null;
  @tracked revenueTab: ValidKpi =
    DEFAULT_QP_VALUES.hotel['revenue-insight'].revenueTab;
  @tracked roomsTab: ValidKpi =
    DEFAULT_QP_VALUES.hotel['revenue-insight'].roomsTab;
  // URL parameter containing the JSON.stringified filters
  @tracked segmentationFilters = '';
  @tracked showFullPortfolio = false;
  @tracked showLoadingPlaceholderState = true;
  @tracked sortHotelsAscending = true;
  @tracked sortHotelsBy: HotelSortBy = HOTEL_SORT_BY.CHANGE;
  @tracked stayDateAggregation: DateAggregationType =
    DEFAULT_QP_VALUES.hotel['revenue-insight'].stayDateAggregation;
  // Staydate filter is separate since it requires custom behaviour,
  @tracked stayDateFilter = '';
  @tracked toDate: string | null = null;

  searchValuesPerSegmentationId: Record<
    FilterableSegmentationBlock,
    { isSearchActive: boolean; searchValue: string }
  >;

  segmentationFilterClickedTask = task(
    { enqueue: true },
    async (segmentationId, segmentationValueId): Promise<void> => {
      // Small timeout just to be able to enqueue this task
      // Makes the page a little more responsive
      await timeout('extra-short');

      // This code is called when the selected filters need to change. The segmentation object of the filter to be updated is passed as a parameter
      // Default behaviour: remove the filter if it's already present in the active filters / add the filter if it's not present in the active filters
      // Exceptions: pickup & pace filters

      // Note: use segmentationValueId as URL parameter.
      // In case of a very long segmentationValue, this will be a truncated version

      // This needs to be looked up here, so all of this state is not saved in the filter bar
      const segmentationObject = getSegmentationValue(
        this.sortedVisibleSegmentations,
        segmentationId,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        segmentationValueId,
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const currentFilters = parseObject(this.segmentationFilters);
      let children = null;
      let parents = null;
      let oneOfParentsIsActive = false;

      if (segmentationObject) {
        children = segmentationObject.children;
        parents = getParents(segmentationObject);
        oneOfParentsIsActive = parents.some((parent) =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          currentFilters[parent.segmentationId as RiBreakdown]?.includes(
            parent.segmentationValueId,
          ),
        );
      }

      const { PACE, PICKUP, SUBSCRIPTION_ID } = RI_BREAKDOWNS;

      switch (segmentationId) {
        case PICKUP:
          this.usageAnalytics.track('Pick-up Filter Applied', {
            eventTrigger: 'pick-up-widget',
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            filterValue: segmentationValueId,
          });
          break;
        case PACE: {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          const startDateString = segmentationValueId.split(':')[0];
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          const endDateString = segmentationValueId.split(':')[1];

          const startDate =
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.hotelAndMarketService.toLocalTime(startDateString);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          const endDate = this.hotelAndMarketService.toLocalTime(endDateString);

          this.usageAnalytics.track('Pace Filter Applied', {
            eventTrigger: 'pace-widget',
            amountOfDays: endDate.diff(startDate, 'days'),
            startDaysOffsetFromToday: startDate.diff(
              this.hotelAndMarketService.getLocalTime(),
              'days',
            ),
          });
          break;
        }
        case SUBSCRIPTION_ID:
          this.usageAnalytics.track('Hotel Included Changed', {
            eventTrigger: 'hotel-widget',
          });
          break;
        default:
          this.usageAnalytics.track('Breakdown Filter Applied', {
            eventTrigger: 'breakdown-widget',
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            filterType: segmentationId,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            filterValue:
              segmentationObject?.segmentationValue ?? segmentationValueId,
          });
      }

      if (segmentationId === PACE || segmentationId === PICKUP) {
        // Specific behaviour for pace and pickup filters
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument
        if (hasFilter(segmentationId, segmentationValueId, currentFilters)) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument
          removeFilter(segmentationId, segmentationValueId, currentFilters);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        } else if (segmentationId === PICKUP && currentFilters[PICKUP]) {
          // There can only be one filter active: remove the current filter if there is already one active
          // Extra edge case for pickup since this can be selected as well
          if (
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            currentFilters[segmentationId].indexOf(segmentationValueId) === -1
          ) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            currentFilters[segmentationId] = [segmentationValueId];
          } else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            delete currentFilters[segmentationId];
          }
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          currentFilters[segmentationId] = [segmentationValueId];
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (segmentationId === PACE && currentFilters[PICKUP]) {
          // A pickup and pace filter can not be active at the same time. Remove any active pickup filters if there are any
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          delete currentFilters[PICKUP];
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (segmentationId === PICKUP && currentFilters[PACE]) {
          // A pickup and pace filter can not be active at the same time. Remove any active pace filters if there are any
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          delete currentFilters[PACE];
        }
      } else if (!oneOfParentsIsActive) {
        // Default behaviour for filters that are not pickup or pace
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument
        toggleFilter(segmentationId, segmentationValueId, currentFilters);
      }

      if (parents) {
        if (oneOfParentsIsActive) {
          const firstActiveParentIndex = parents.findIndex((parent) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            currentFilters[parent.segmentationId as string].includes(
              parent.segmentationValueId,
            ),
          );
          const activeParents = parents.splice(firstActiveParentIndex);

          this.handleActiveParent(
            activeParents,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            currentFilters,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            segmentationId,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            segmentationValueId,
          );
        }

        // Do the following check for all parents (in the order of bottom to top).
        parents.reverse().forEach((parent) => {
          // If you complete all children, the parent will be selected instead.
          if (
            parent.children &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            hasAllSelected(parent.children, currentFilters) &&
            parent.isSelectable &&
            parent.segmentationId &&
            parent.segmentationValueId
          ) {
            addFilter(
              parent.segmentationId,
              parent.segmentationValueId,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              currentFilters,
            );
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            removeFilters(parent.children, currentFilters);
          }
        });
      }

      // When selecting a parent, remove all of its children from the filters (including children of children, etc).
      if (children && children.length) {
        const nestedChildren =
          MarketRevenueInsightController.getAllNestedChildren(
            segmentationObject,
          );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        removeFilters(nestedChildren, currentFilters);
      }

      // Commit whatever changes happend to parsedSegmentationFilters into the query params filter
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.setSegmentationFilters(stringifyObject(currentFilters));
    },
  );

  showUnavailableMessageTask = task({ drop: true }, async (): Promise<void> => {
    await timeout();
    this.toast.alert(
      this.intl.t('revenue_insight.filters.unavailable_message'),
      {
        showIcon: true,
      },
    );
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datePickerMaxDate() {
    const singlePropertyMaxDate = this.hotelAndMarketService.ratesDataEnd;
    const endOfMaxDateMonth = singlePropertyMaxDate.endOf('month');

    if (singlePropertyMaxDate.isSame(endOfMaxDateMonth, 'day')) {
      return singlePropertyMaxDate;
    }

    // Ensure that the max date is always the end of the month
    return singlePropertyMaxDate.subtract(1, 'month').endOf('month');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datePickerMinDate() {
    let minDate =
      this.hotelAndMarketService.hotelOrMarket
        .pmsFirstCompleteStayDateWithDefault;

    // For multi property the minimum always needs to be on the first
    if (minDate.date() !== 1) {
      // if it's not we take the start of the next month, after the min date
      minDate = minDate.add(1, 'month').startOf('month');
    }

    return minDate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datePickerRangeOptions() {
    const todayMoment = this.hotelAndMarketService.getLocalTime();

    // All possible quick select options for the datepicker
    let options = [
      {
        text: 'revenue_insight.current_month',
        id: 'current_month',
        isSelected: true,
        range: {
          start: todayMoment.startOf('month'),
          end: todayMoment.endOf('month').startOf('day'),
        },
      },
      {
        text: 'revenue_insight.current_month_plus',
        count: 2,
        id: 'current_month_plus_2',
        isSelected: false,
        range: {
          start: todayMoment.startOf('month'),
          end: todayMoment.add(2, 'month').endOf('month'),
        },
      },
      {
        text: 'revenue_insight.current_quarter',
        id: 'quarter',
        isSelected: false,
        range: {
          start: todayMoment.startOf('quarter'),
          end: todayMoment.endOf('quarter'),
        },
      },
      {
        text: 'revenue_insight.year_to_date',
        id: 'year_to_date',
        isSelected: false,
        range: {
          // This is a special range. You want YTD to go to the last final month
          // Looking at YTD on March 15th will select Jan & Feb
          // Looking at YTD on Jan 15th will select the full previous year
          // Easiest way to do this is to always subtract a month and then calculate YTD
          start: todayMoment.subtract(1, 'month').startOf('year'),
          end: todayMoment.subtract(1, 'month').endOf('month'),
        },
      },
      {
        text: 'revenue_insight.full_year',
        id: 'full_year',
        isSelected: false,
        range: {
          start: todayMoment.startOf('year'),
          end: todayMoment.endOf('year'),
        },
      },
      {
        text: 'general.custom',
        id: 'custom',
        isSelected: false,
        range: {
          start: todayMoment.startOf('month'),
          end: todayMoment.endOf('month').startOf('day'),
        },
      },
    ];

    // Filter out all of the options which start before the integration date
    options = options.filter(
      (option) => option.range.start >= this.datePickerMinDate,
    );

    return [
      {
        id: 'references',
        title: this.intl.t('revenue_insight.report_selected_period').toString(),
        options,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datePickerRefMaxDate() {
    return this.fromDateMoment.subtract(1, 'day');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datePickerRefMinDate() {
    return this.hotelAndMarketService.ratesDataStart;
  }

  // Segmentation block expects sorted segmentations, so we'll need to sort this as well
  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get extraSortOptions() {
    const { bobSortKey } = this.riSelections;
    return {
      sortType: bobSortKey,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get favourites() {
    return this.hotelOrMarket.favouriteFilters.slice();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filterDisplayNameOverride() {
    // For RI we do the display name formatting based on segmentationValue instead of segmentationValueId
    // Create a mapping that can be used to lookup the value of a value_id
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const filters: Record<RiBreakdown, string[]> =
      parseObject(this.segmentationFilters) ?? {};
    const filterDisplayNameOverride: Partial<
      Record<RiBreakdown, Record<string, string>>
    > = {};
    (Object.keys(filters) as RiBreakdown[]).forEach((filterKey) => {
      // In MP we can filter on hotels, this would put an entry for subscription_id in the dict.
      // We don't need display names for that, only for breakdowns.
      if (Object.values(RI_BREAKDOWNS).includes(filterKey)) {
        filterDisplayNameOverride[filterKey] =
          this.buildSegmentDisplayNameOverrideForBreakdown(
            filterKey,
            filters[filterKey],
          );
      }
    });

    return filterDisplayNameOverride;
  }

  /**
   * This CP determines if we can still filter breakdowns
   * It return an object that has counts how many filters you can still click in that breakdown
   * example:
   * {
   *   travel_agent: 5,
   *   pickup: 0
   * }
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filterLimits() {
    const limits = {};

    FILTERABLE_SEGMENTATION_BLOCKS.forEach((blockId) => {
      if (
        this.riSelections.isTargetActive &&
        blockId !== RI_BREAKDOWNS.IMPACT_DATE
      ) {
        // No filters available if you are looking at budget or forecast
        // @ts-expect-error: Incorrect type
        limits[blockId] = 0;
      } else if (
        this.stayDateFilter !== '' &&
        blockId !== RI_BREAKDOWNS.IMPACT_DATE
      ) {
        // @ts-expect-error: Incorrect type
        limits[blockId] = 0;
      } else if (!this.riSelections.hasFiltersOtherThanHotel) {
        // If there are no current filters active, every filter has the max amount available
        // @ts-expect-error: Incorrect type
        limits[blockId] = MAX_AMOUNT_OF_FILTERS_PER_BLOCK;
      } else if (this.parsedSegmentationFilters[blockId]) {
        // If the current breakdown is being filtered, you have the max - the current amount of filters left
        // @ts-expect-error: Incorrect type
        limits[blockId] =
          MAX_AMOUNT_OF_FILTERS_PER_BLOCK -
          this.parsedSegmentationFilters[blockId].length;
      } else {
        // if none of these cases apply that means another breakdown is currently being filtered and that you can't filter on this one
        // @ts-expect-error: Incorrect type
        limits[blockId] = 0;
      }
    });

    // These are always zero
    // @ts-expect-error: Incorrect type
    limits[RI_BREAKDOWNS.PACE] = 0;
    // @ts-expect-error: Incorrect type
    limits[RI_BREAKDOWNS.PICKUP] = 0;
    // This one can always be filtered and has an unlimited amount of filters
    // @ts-expect-error: Incorrect type
    limits[RI_BREAKDOWNS.SUBSCRIPTION_ID] = Infinity;

    return limits;
  }

  /**
   * Checks if any segmentation filter is active
   * @return { boolean }   : return if any filter is active
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filtersActive() {
    return this.segmentationFilters.length > 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get fromDateMoment() {
    return this.fromDate
      ? this.hotelAndMarketService.toLocalTime(this.fromDate)
      : this.hotelAndMarketService.getLocalTime().startOf('month');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasHotelsThatMapByRateCode() {
    // Check filled_in_by_frontend so we only show info about filtered results
    const hotels =
      this.sortedSegmentationHotels?.sortedSegmentationObjects?.filter(
        // @ts-expect-error: Incorrect type
        (hotel) => !hotel.filled_in_by_frontend,
      );
    return hotels.some(
      (hotel) => hotel.meta?.segment_breakdown === MAPPING_TYPE_RATE_CODE,
    );
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    return this.hotelAndMarketService.hotel;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelOrMarket() {
    return this.hotelAndMarketService.hotelOrMarket;
  }

  @computed(
    `parsedSegmentationFilters.${RI_BREAKDOWNS.SUBSCRIPTION_ID}.[]`,
    'parsedSegmentationFilters',
    'showFullPortfolio',
    'sortedSegmentationHotels.sortedSegmentationObjects.[]',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelsCount() {
    if (!this.sortedSegmentationHotels?.sortedSegmentationObjects) {
      // If it's still loading don't return anything
      return null;
    }

    if (this.parsedSegmentationFilters[RI_BREAKDOWNS.SUBSCRIPTION_ID]) {
      // If a hotel filter is active, just return the amount of filtered hotels
      return this.parsedSegmentationFilters[RI_BREAKDOWNS.SUBSCRIPTION_ID]
        ?.length;
    }

    if (this.showFullPortfolio) {
      // If all hotels are selected just return all of them
      return this.sortedSegmentationHotels.sortedSegmentationObjects.length;
    }

    // If not, just return those w/o issues
    return this.sortedSegmentationHotels.sortedSegmentationObjects.filter(
      // @ts-expect-error: Incorrect type
      (segmentation) => !segmentation.meta || !segmentation.meta.issues,
    ).length;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelsWithIssues() {
    const hotels = this.sortedSegmentationHotels?.sortedSegmentationObjects;
    // Check filled_in_by_frontend so we only show info about filtered results
    return hotels.filter(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (hotel) => hotel.meta?.issues?.length > 0 && !hotel.filled_in_by_frontend,
    );
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isClosed() {
    return (
      this.hotel?.revenueInsightClosureDate &&
      this.hotel?.revenueInsightClosureDate.isBefore(
        this.hotelAndMarketService.getLocalTime(),
        'day',
      )
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDayAggregation() {
    return this.stayDateAggregation === 'day';
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDemoProperty() {
    return this.hotel?.isDemoProperty;
  }

  // SP: if selected item has a parent in the filters, remove the parent from filters and select all of it's children (except for the one you clicked on).
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isFavouritesDisabled() {
    return this.riSelections.isTargetActive;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isModelLoading() {
    return this.model.modelTask?.isRunning;
  }

  // Keep track of which issues the market is having
  // Dict keys are the translation suffixes e.g. out_of_sync in revenue_insight.warning.out_of_sync
  // Dict values are an indicator for the number of times this type of issue occurs
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get issueDict() {
    const issueDict = {};
    this.hotelsWithIssues.forEach((hotel) => {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      hotel.meta.issues.forEach((issue) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        let issueKey = issue;

        // Combine budget and forecast issues
        if (issue === 'no_budget' || issue === 'no_forecast') {
          issueKey = 'no_targets';
        }

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (!issueDict[issueKey]) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          issueDict[issueKey] = 1;
        } else {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          issueDict[issueKey] += 1;
        }
      });
    });

    // Translation for no_ly issues varies depending on the current view (vs LY <-> vs Target) and other types of issues in the market
    // @ts-expect-error: Incorrect type
    if (issueDict.no_ly && this.showPickupLY) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const lyValue = issueDict.no_ly;
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const hasTargetIssue = issueDict.no_targets;

      if (this.riSelections.isTargetActive) {
        if (hasTargetIssue) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          issueDict.no_ly_ref_targets_with_target_issue = lyValue;
        } else {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          issueDict.no_ly_ref_targets = lyValue;
        }
      } else {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        issueDict.no_ly_ref_ly = lyValue;
      }

      // @ts-expect-error: Incorrect type
      delete issueDict.no_ly;
    }

    return issueDict;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get kpiBlocks() {
    const nrOfRoomsAndOccupancyBlock = {
      id: 'occupancy',
      kpis: [KPIS.OCCUPANCY, KPIS.NR_OF_ROOMS],
      activeTab: this.roomsTab,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      changeTab: (kpi: ValidKpi) => {
        this.changeSelectedKpi(kpi);
        // eslint-disable-next-line ember/no-side-effects
        this.roomsTab = kpi;
      },
    };

    const adrBlock = {
      id: 'adr',
      kpis: [KPIS.ADR],
      activeTab: KPIS.ADR,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      changeTab: (kpi: ValidKpi) => {
        this.changeSelectedKpi(kpi);
      },
    };

    const revenueAndRevparBlock = {
      id: 'revenue',
      kpis: [KPIS.REVENUE, KPIS.REVPAR],
      activeTab: this.revenueTab,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      changeTab: (kpi: ValidKpi) => {
        this.changeSelectedKpi(kpi);
        // eslint-disable-next-line ember/no-side-effects
        this.revenueTab = kpi;
      },
    };

    return [nrOfRoomsAndOccupancyBlock, adrBlock, revenueAndRevparBlock];
  }

  /**
   * This CP determines the actual kpi per breakdown.
   * We need it because impact_date and subscription_id are the only breakdowns where we want to show occupancy.
   * It returns an object that maps the correct kpi to the id.
   * Example:
   * {
   *   travel_agent: 'nrOfRooms',
   *   impact_date: 'occupancy'
   * }
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get kpiPerBreakdown() {
    const kpiPerBreakdown: Partial<Record<RiBreakdown, ValidKpi>> = {};

    FILTERABLE_SEGMENTATION_BLOCKS.forEach((blockId) => {
      kpiPerBreakdown[blockId] =
        KPIS_TO_REPLACE[this.kpi as keyof typeof KPIS_TO_REPLACE] ?? this.kpi;
    });

    kpiPerBreakdown[RI_BREAKDOWNS.IMPACT_DATE] = this.kpi;
    kpiPerBreakdown[RI_BREAKDOWNS.SUBSCRIPTION_ID] = this.kpi;

    return kpiPerBreakdown as Record<RiBreakdown, ValidKpi>;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numOfHotelsWithIssues() {
    return this.hotelsWithIssues.length;
  }

  // We parse the moment objects instead of using fromDate,toDate & refFromDate directly to make sure the defaults aren't null
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get parsedFromDate() {
    return this.fromDateMoment.format('YYYY-MM-DD');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get parsedRefFromDate() {
    return this.refFromDateMoment.format('YYYY-MM-DD');
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get parsedSegmentationFilters() {
    return this.riSelections.parsedSegmentationFilters;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get parsedStayDateFilter() {
    return this.riSelections.parsedStayDateFilter;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get parsedToDate() {
    return this.toDateMoment.format('YYYY-MM-DD');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get range() {
    return {
      start: this.fromDateMoment,
      end: this.toDateMoment,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get refDatePickerRangeOptions() {
    let { start } = this.range;
    if (!start) {
      start = dayjs();
    }

    const swly = start.subtract(WEEKS_IN_YEAR, 'week');
    const sdly = start.subtract(1, 'year');

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const sdlyEnd = getSdlyEnd(start, sdly, this.referencePeriod);

    const sidebarLists = [
      {
        id: 'references',
        title: this.intl.t('datepicker.options.vs_reference').toString(),
        options: [
          {
            dropdownText: 'revenue_insight.swly_short',
            text: 'revenue_insight.swly_long',
            id: 'SWLY',
            isSelected: true,
            range: {
              start: swly,
              end: swly.add(this.referencePeriod, 'day'),
            },
          },
          {
            dropdownText: 'revenue_insight.sdly_short',
            text: 'revenue_insight.sdly_long',
            id: 'SDLY',
            isSelected: false,
            range: {
              start: sdly,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              end: sdlyEnd,
            },
          },
        ],
      },
      {
        id: 'targets',
        title: this.intl.t('datepicker.options.vs_target').toString(),
        options: [
          {
            dropdownText: 'revenue_insight.budget',
            text: 'revenue_insight.budget',
            id: 'budget',
            isDisabled: false,
            isSelected: false,
            // We solely apply a range pointing to the current month here
            // Because this puts the calendar to a place grayed out in the calendar, indicating it's disabled...
            // For budget, the range does not matter anyway
            range: {
              start: null,
              end: null,
            },
          },
          {
            dropdownText: 'general.forecast',
            text: 'general.forecast',
            id: 'forecast',
            isDisabled: false,
            isSelected: false,
            // We solely apply a range pointing to the current month here
            // Because this puts the calendar to a place grayed out in the calendar, indicating it's disabled...
            // For forecast, the range does not matter anyway
            range: {
              start: null,
              end: null,
            },
          },
        ],
      },
    ];

    return sidebarLists;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referencePeriod() {
    if (this.fromDateMoment && this.toDateMoment) {
      return this.toDateMoment.diff(this.fromDateMoment, 'day');
    }
    return 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get refFromDateMoment() {
    // Subtracting WEEKS_IN_YEAR gives us SWLY
    return this.refFromDate
      ? this.hotelAndMarketService.toLocalTime(this.refFromDate)
      : this.hotelAndMarketService
          .getLocalTime()
          .startOf('month')
          .subtract(WEEKS_IN_YEAR, 'week');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get refRange() {
    return {
      start: this.refFromDateMoment,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      end: this.refToDateMoment,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get refToDateMoment() {
    const { fromDateMoment, refFromDateMoment, referencePeriod, reference } =
      this;

    if (referencePeriod >= 0) {
      if (reference === 'SDLY') {
        // Only remove a day when SDLY is selected
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return getSdlyEnd(
          fromDateMoment,
          refFromDateMoment,
          this.referencePeriod,
        );
      }
      return refFromDateMoment.add(referencePeriod, 'day');
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get riCurrency() {
    return this.hotelAndMarketService.hotelOrMarket?.revenueInsightCurrency;
  }

  /**
   * Determines order of the segmentation blocks
   * Based on the order available on the hotel object
   * @return { array }  : array of object which contain the key and whether they are visible. The array is in the order in which the blocks should appear
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get segmentationBlocksOrder() {
    const userSegmentationOrder = this.segmentationOrder;
    const userSegmentationCategoryMappings = this.segmentationCategoryMappings;

    // Copy the default (this is an object, so modifying would modify the default, which we do not want)
    const defaultBlocksCopy = cloneDeep(this.defaultSegmentationBlocksOrder);

    // Hide non-supported breakdowns for the current subscription
    defaultBlocksCopy.forEach((segmentation) => {
      segmentation.isSupported = this.isSegmentationSupported(segmentation);
    });

    // if there is no order yet, simply display the default
    if (!userSegmentationOrder || userSegmentationOrder.length === 0) {
      return defaultBlocksCopy;
    }

    // create two arrays up front, this will enable us to get the correct order of the invisibleSegmentation by just filtering
    // the order as in the current array should be kept
    // The visible segmentations will have to be filtered again
    const visibleSegmentations: SegmentationBlock[] = [];
    const invisibleSegmentations: SegmentationBlock[] = [];

    defaultBlocksCopy.forEach((segmentation) => {
      const index = userSegmentationOrder.indexOf(segmentation.segmentationKey);
      if (index !== -1) {
        // Saving this index on the object will enable us to sort the items, without looping over them again
        segmentation.userSegmentationOrderIndex = index;
        segmentation.isVisible = true;
        visibleSegmentations.push(segmentation);
      } else {
        // Need to set this to invisible (visible by default)
        segmentation.isVisible = false;
        invisibleSegmentations.push(segmentation);
      }
      segmentation.isMappingVisible =
        userSegmentationCategoryMappings.indexOf(
          segmentation.segmentationKey,
        ) !== -1;
    });

    // Now we still need to sort the visible segmentations in the order provided by the API
    // And concat the invisible ones afterwards
    const result = visibleSegmentations
      .sort(
        (a, b) =>
          (a.userSegmentationOrderIndex ?? 0) -
          (b.userSegmentationOrderIndex ?? 0),
      )
      .concat(invisibleSegmentations);

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get segmentationCategoryMappings() {
    return this.hotelAndMarketService.user?.riMpSegmentationCategoryMappings;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get segmentationOrder() {
    return this.hotelAndMarketService.user?.riMpSegmentationOrder;
  }

  @computed('model.segmentations')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get segmentations() {
    return this.model.segmentations;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedStayDateAggregationInDropdown() {
    return this.stayDateAggregationOptions.filter(
      (category) => category.id === this.stayDateAggregation,
    )[0];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showBanner() {
    return this.numOfHotelsWithIssues > 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showPickupLY() {
    return this.riSelections.showPickupLY;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showStayDateFilterLimit() {
    return (
      // @ts-expect-error: Incorrect type
      this.isDayAggregation && this.filterLimits[RI_BREAKDOWNS.IMPACT_DATE] > 0
    );
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedSegmentationHotels() {
    const segmentationHotels = createSegmentationBlockStructure(
      RI_BREAKDOWNS.SUBSCRIPTION_ID,
      this.segmentations[RI_BREAKDOWNS.SUBSCRIPTION_ID] ?? [],
    );

    const isSortedByChange = this.sortHotelsBy === HOTEL_SORT_BY.CHANGE;
    let sortType = isSortedByChange ? 'change' : 'BOB';
    if (this.riSelections.isBudgetActive) {
      sortType = isSortedByChange ? 'budgetChange' : 'budget';
    } else if (this.riSelections.isForecastActive) {
      sortType = isSortedByChange ? 'forecastChange' : 'forecast';
    }

    const extraOptions = {
      sortBy: this.sortHotelsBy, // Kpi or change column
      sortType, // BOB, budget, forecast, change, budgetChange or forecastChange
      sortOrder: this.sortHotelsAscending ? 'asc' : 'desc',
    };
    return this.mapSortableSegmentation(segmentationHotels, extraOptions);
  }

  // Segmentation block expects sorted segmentations, so we'll need to sort this as well
  @computed('segmentations.pickup.[]', 'extraSortOptions')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedSegmentationPickup() {
    const segmentationPickup = createSegmentationBlockStructure(
      RI_BREAKDOWNS.PICKUP,
      this.segmentations?.[RI_BREAKDOWNS.PICKUP] ?? [],
    );
    return this.mapSortableSegmentation(
      segmentationPickup,
      this.extraSortOptions,
    );
  }

  @computed(
    'kpi',
    'riSelections.{isBudgetActive,isForecastActive}',
    'segmentations',
    'isModelLoading',
    'visibleSegmentationIds.[]',
    'extraSortOptions',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedVisibleSegmentations() {
    const groupedSegmentations: Partial<
      Record<RiBreakdown, ReturnType<typeof this.mapSortableSegmentation>>
    > = {};
    // Creating an array of objects containing the segmentation objects grouped by segmentation
    // We add them in the order indicated in the segmentationBlocksOrder array
    this.visibleSegmentationIds
      .filter(
        (segmentationKey) =>
          !(DEFAULT_BREAKDOWNS as RiBreakdown[]).includes(segmentationKey),
      )
      .forEach((segmentationKey) => {
        const segmentationBlockStructure = createSegmentationBlockStructure(
          segmentationKey,
          this.segmentations?.[segmentationKey] ?? [],
        );
        groupedSegmentations[segmentationKey] = this.mapSortableSegmentation(
          segmentationBlockStructure,
          this.extraSortOptions,
        );
      });

    return groupedSegmentations;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get stayDateAggregationOptions() {
    return [
      { id: 'day', label: this.intl.t('general.day').toString() },
      { id: 'week', label: this.intl.t('general.week').toString() },
      { id: 'month', label: this.intl.t('general.month').toString() },
    ];
  }

  @computed(
    `model.segmentations.${RI_BREAKDOWNS.IMPACT_DATE}.[]`,
    'isModelLoading',
    'model.segmentations',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get stayDateData() {
    if (!this.isModelLoading) {
      const stayDates = this.model.segmentations[RI_BREAKDOWNS.IMPACT_DATE];
      MarketRevenueInsightController.mapOccupancyData(stayDates);
      return stayDates;
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get toDateMoment() {
    return this.toDate
      ? this.hotelAndMarketService.toLocalTime(this.toDate)
      : this.hotelAndMarketService.getLocalTime().endOf('month');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get updateTimestamp() {
    if (this.isDemoProperty) {
      // Fixed dummy updateTimestamp of "6 hours ago"
      return this.hotelAndMarketService.getLocalTime().subtract(6, 'hour');
    }

    if (this.isClosed) {
      return this.hotel?.revenueInsightClosureDate;
    }

    if (
      'pmsIntegrationLastUpdate' in this.hotelOrMarket &&
      this.hotelOrMarket.pmsIntegrationLastUpdate
    ) {
      return this.hotelOrMarket.pmsIntegrationLastUpdate;
    }

    return null;
  }

  // Returns all of the KPIs of the active tabs in the KPI blocks.
  // In some breakdowns we show different visible KPIs, for those we replace them.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get visibleKpisPerBreakdown() {
    const visibleKpisPerBreakdown: Partial<Record<RiBreakdown, ValidKpi[]>> =
      {};

    const visibleKpis = this.kpiBlocks.map((kpiBlock) => kpiBlock.activeTab);
    const replacedVisibleKpis = this.kpiBlocks.map(
      (kpiBlock) =>
        KPIS_TO_REPLACE[kpiBlock.activeTab as keyof typeof KPIS_TO_REPLACE] ??
        kpiBlock.activeTab,
    );

    FILTERABLE_SEGMENTATION_BLOCKS.forEach((blockId) => {
      visibleKpisPerBreakdown[blockId] = replacedVisibleKpis;
    });

    visibleKpisPerBreakdown[RI_BREAKDOWNS.ALL] = visibleKpis;
    visibleKpisPerBreakdown[RI_BREAKDOWNS.IMPACT_DATE] = visibleKpis;
    visibleKpisPerBreakdown[RI_BREAKDOWNS.SUBSCRIPTION_ID] = visibleKpis;

    return visibleKpisPerBreakdown;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get visibleSegmentationIds() {
    // Get all segmentations that should be shown
    let segmentationBlocksOrder = this.segmentationBlocksOrder.filter(
      (block) => block.isVisible,
    );

    segmentationBlocksOrder = this.filterVisibleSegmentations(
      segmentationBlocksOrder,
    );

    return segmentationBlocksOrder.map(
      (segmentationBlock) => segmentationBlock.segmentationKey,
    );
  }

  constructor() {
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    // Make a default empty search state for all blocks where you can filter
    this.searchValuesPerSegmentationId = FILTERABLE_SEGMENTATION_BLOCKS.reduce(
      (acc, segmentationId) => {
        acc[segmentationId] = {
          searchValue: '',
          isSearchActive: false,
        };
        return acc;
      },
      {} as typeof this.searchValuesPerSegmentationId,
    );

    this.defaultSegmentationBlocksOrder = cloneDeep(
      DEFAULT_SEGMENTATION_BLOCKS_ORDER,
    );

    // Set everything after the max blocks to false
    for (
      let i = MAX_AMOUNT_OF_BLOCKS;
      i < DEFAULT_SEGMENTATION_BLOCKS_ORDER.length;
      i++
    ) {
      // @ts-expect-error: Incorrect type
      this.defaultSegmentationBlocksOrder[i].isVisible = false;
    }
  }

  // Creates a dict with a mapping per breakdown of segmentation_value_ids to their respective segmentation_values
  // e.g. {'parent-travel-agent-...': 'Travel agent', ''}
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  buildSegmentDisplayNameOverrideForBreakdown(
    breakdown: RiBreakdown,
    segmentationValueIds: string[],
  ) {
    const segmentationDisplayNameOverride: Record<string, string> = {};
    segmentationValueIds.forEach((segmentationValueId) => {
      const segmentationItem = this.findSegmentationItemForValueId(
        this.segmentations[breakdown] ?? [],
        segmentationValueId,
      );

      // Check if the segmentationItem was found, it can be empty for the initial page load when the API response is not returned
      if (segmentationItem && segmentationItem.segmentationValue) {
        segmentationDisplayNameOverride[segmentationValueId] =
          segmentationItem.segmentationValue;
      }
    });

    return segmentationDisplayNameOverride;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  calculatePPstart(start: Dayjs) {
    const startingDay = start.isoWeekday();
    const previousPeriodStart = start.subtract(this.referencePeriod, 'day');
    if (startingDay < previousPeriodStart.isoWeekday()) {
      return previousPeriodStart.isoWeekday(startingDay);
    }
    return previousPeriodStart.subtract(7, 'day').isoWeekday(startingDay);
  }

  /**
   * This check will compare the available segmentationFilters with the user visible blocks
   * If there are any filters present that are not visible for the user, we will show a warning
   * After clicking ok, we remove the missing filters
   * @param  { string } segmentationFilters : A string representation of the filters, will be the empty string if no filters are present
   * @param  { boolean } displayMessage : only display a message when on initial visit, otherwise by default false
   */
  async checkForMissingBlocks(
    // @ts-expect-error: Incorrect type
    segmentationFilters,
    displayMessage = false,
  ): Promise<void> {
    // Don't need to do anything if there are no filters
    if (segmentationFilters) {
      // First parse the filters
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment
      const parsedSegmentationFilters = JSON.parse(segmentationFilters);
      const visibleBlockIds = this.visibleSegmentationIds;

      const missingSegmentations = Object.keys(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        parsedSegmentationFilters,
      ).filter(
        // Loop over the filters and select those that are missing
        (filter) => {
          return (
            // @ts-expect-error: Incorrect type
            visibleBlockIds.indexOf(filter) === -1 &&
            // @ts-expect-error: Incorrect type
            ALL_SELECTABLE_SEGMENTATION_BLOCKS.indexOf(filter) !== -1
          );
        },
      );

      // If there are missing filters, show the error message
      if (missingSegmentations.length > 0) {
        if (displayMessage) {
          await this.alerting.showWarningAlert({
            title: this.intl.t('revenue_insight.missing_block.title', {
              count: missingSegmentations.length,
            }),
            text: this.intl.t('revenue_insight.multi.missing_block.content'),
            buttons: {
              confirm: {
                text: this.intl.t('general.OK'),
              },
            },
          });
        }

        this.removeFiltersForHiddenBlocks(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          parsedSegmentationFilters,
          missingSegmentations,
        );
      }
    }
  }

  /**
   * Calls the export endpoint
   * Can do this for a specific segment
   * This is a function so that we can use it in the breakdown-detail controller
   * @param  { string } segment : id of a specific breakdown
   */

  exportSegmentData(segment?: RiBreakdown): void {
    const baseParams = this.getBaseExcelExportParams(segment);
    const extraParams = this.getExtraExcelExportParams();
    const params = Object.assign(baseParams, extraParams) as Record<
      string,
      unknown
    >;

    if (params['roomsTab'] === KPIS.NR_OF_ROOMS) {
      params['roomsTab'] = 'nr_of_rooms';
    }

    const url = formatUrl(
      '/apigateway/v1/app/download/revenueinsight/multiproperty',
      params,
      this.ajax.host,
    );

    openUrl(url);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  filterVisibleSegmentations(segmentationBlocksOrder: SegmentationBlock[]) {
    let supportedSegmentationBlocksOrder = segmentationBlocksOrder;

    if (this.riSelections.isSingleProperty) {
      supportedSegmentationBlocksOrder =
        supportedSegmentationBlocksOrder.filter(
          (block) =>
            // @ts-expect-error: Object is possibly 'null'.
            this.hotel.revenueInsightSupportedAggregations.indexOf(
              block.segmentationKey,
            ) !== -1,
        );
    }

    supportedSegmentationBlocksOrder = supportedSegmentationBlocksOrder.filter(
      (block) =>
        (CANCELLATION_ONLY_BLOCKS as RiBreakdown[]).indexOf(
          block.segmentationKey,
        ) === -1,
    );

    const segmentations = this.riSelections.isTargetActive
      ? supportedSegmentationBlocksOrder.filter(
          (block) =>
            (TARGET_BLOCKS as RiBreakdown[]).indexOf(block.segmentationKey) !==
            -1,
        )
      : supportedSegmentationBlocksOrder;

    return segmentations.slice(0, MAX_AMOUNT_OF_BLOCKS);
  }

  // Recursively finds the segmentation item for a given segmentation value id in a list of items and their children
  findSegmentationItemForValueId(
    segmentationItems: SegmentationValueLike[],
    segmentationValueId: string,
  ): SegmentationValueLike | null {
    for (let i = 0; i < segmentationItems.length; i++) {
      const segmentationItem = segmentationItems[i] as SegmentationValueLike;
      // Return if the current item matches
      if (segmentationItem.segmentationValueId === segmentationValueId) {
        return segmentationItem;
      }
      // Otherwise recursively check if it's one of the children
      if (segmentationItem.children) {
        const childItem = this.findSegmentationItemForValueId(
          segmentationItem.children,
          segmentationValueId,
        );
        if (childItem) {
          return childItem;
        }
      }
    }
    return null;
  }

  // Returns all the nested children (children, grandchildren, and so on) of a segmentation object in an array.
  static getAllNestedChildren(
    segmentationObject: SegmentationValueLike,
  ): SegmentationValueLike[] {
    const offspring: SegmentationValueLike[] = [];

    if (segmentationObject.children) {
      segmentationObject.children.forEach((child) => {
        offspring.push(child);

        offspring.push(
          ...MarketRevenueInsightController.getAllNestedChildren(child),
        );
      });
    }

    return offspring;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getBaseExcelExportParams(segment?: RiBreakdown) {
    let fromDate = this.parsedFromDate;
    let toDate = this.parsedToDate;

    if (this.stayDateFilter) {
      const { parsedStayDateFilter } = this;
      if (parsedStayDateFilter?.startDate && parsedStayDateFilter?.endDate) {
        fromDate = parsedStayDateFilter.startDate;
        toDate = parsedStayDateFilter.endDate;
      }
    }

    // Take the stay date filter offset into account for the export
    const { stayDateFilterOffset } = getDateRange(
      this.parsedFromDate,
      this.parsedToDate,
      this.hotelAndMarketService,
      this.stayDateFilter,
    );

    const params: Record<string, unknown> = {
      fromDate,
      toDate,
      refFromDate: this.refRange.start

        .add(stayDateFilterOffset, 'day')
        .format('YYYY-MM-DD'),
      segmentationFilters: this.segmentationFilters,
      refPeriod: this.reference === 'PP' ? 'custom' : this.reference,
      stay_date_aggregation: this.stayDateAggregation,
      disable_pickup_ly: !this.showPickupLY,
      currency: this.riSelections.riCurrency,
    };

    if (segment) {
      params['segment'] = segment;
    }

    const categoryAggregations = this.segmentationCategoryMappings.filter(
      (category) => category !== 'isLoading',
    );
    if (categoryAggregations) {
      params['category_aggregations[]'] = categoryAggregations;
    }

    return params;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getExtraExcelExportParams() {
    // As soon as one hotel in the portfolio uses the KPI display based on their hierarchy we only export the selected KPI category
    const kpiCategories =
      this.sortedSegmentationHotels?.sortedSegmentationObjects?.some(
        (hotel) => hotel?.meta?.kpi_display === KPI_DISPLAY_CUSTOM_MAPPING,
      )
        ? [this.kpiCategory]
        : this.riSelections.availableKpiCategories;

    const visibleKpisParams = {
      revenueTab: this.revenueTab,
      roomsTab: this.roomsTab,
    };

    return {
      market_id: this.hotelOrMarket.id,
      full_portfolio: this.showFullPortfolio,
      'kpi_categories[]': kpiCategories,
      ...visibleKpisParams,
    };
  }

  // MP: if selected item has a parent in the filters, remove the parent from filters and only select the item you clicked on.

  handleActiveParent(
    // @ts-expect-error: Incorrect type
    activeParents,
    currentFilters: Record<string, string[]>,
    segmentationId: string,
    segmentationValueId: string,
  ): void {
    // Remove all parents from the filters.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    removeFilters(activeParents, currentFilters);

    // Add the clicked item to the filters.
    addFilter(segmentationId, segmentationValueId, currentFilters);
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isSegmentationSupported(segmentation) {
    // @ts-expect-error: Incorrect type
    return this.hotelAndMarketService.hotelOrMarket?.revenueInsightSupportedAggregations.includes(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      segmentation.segmentationKey,
    );
  }

  // @ts-expect-error: Incorrect type

  static mapOccupancyData(stayDates): void {
    // stay date chart expects a totalCapacity property
    // This is because in single RI we need to merge this from the rates request
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    stayDates?.forEach((stayDate) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      set(stayDate, 'totalCapacity', stayDate.value?.capacity);
    });
  }

  mapSortableSegmentation(
    segmentation: SegmentationBlockStructure,
    extraOptions: { sortType: string },
  ): {
    kpi: ValidKpi;
    segmentationName: RiBreakdown;
    sortedSegmentationObjects: SegmentationValueLike[];
  } {
    const { segmentationName, segmentationObjects } = segmentation;

    const sortedSegmentationObjects = sortValues(
      segmentationObjects,
      segmentationName,
      this.kpiPerBreakdown[segmentationName],
      this.parsedSegmentationFilters,
      extraOptions,
    );

    return {
      segmentationName,
      sortedSegmentationObjects,
      kpi: this.kpi,
    };
  }

  /**
   * If he has hidden a block where filters are active for, these need to be removed
   * @param { object} parsedSegmentationFilters : segmentation filters as an object
   * @param { array } missingSegmentations      : array of missing segmentation filters
   * @param { object } transition
   * @param { objects } params                  : all params
   */

  removeFiltersForHiddenBlocks(
    parsedSegmentationFilters: ParsedFilters,
    missingSegmentations: string[],
  ): void {
    missingSegmentations.forEach((segmentation) => {
      delete parsedSegmentationFilters[segmentation];
    });

    if (Object.keys(parsedSegmentationFilters).length === 0) {
      this.setSegmentationFilters('');
    } else {
      this.setSegmentationFilters(JSON.stringify(parsedSegmentationFilters));
    }
  }

  setSegmentationFilters(filters: string): void {
    this.segmentationFilters = filters;
  }

  setStayDateFilter(filters: string): void {
    this.showLoadingPlaceholderState = true;
    this.stayDateFilter = filters;
  }

  @action changeSelectedKpi(kpi: ValidKpi): void {
    this.kpi = kpi;
  }

  @action changeSelectedKpiCategory(kpiCategory: KpiCategory): void {
    this.kpiCategory = kpiCategory;
  }

  @action chartSelectionChanged(
    id: RiBreakdown,
    range: { end?: Dayjs; start?: Dayjs },
  ): void {
    if (range.start && range.end) {
      // TODO: correct timezones
      const strResult = `${range.start.format('YYYY-MM-DD')}:${range.end.format('YYYY-MM-DD')}`;

      if (id === RI_BREAKDOWNS.IMPACT_DATE) {
        this.send('stayDateFilterClicked', id, strResult);
      } else if (!this.riSelections.isTargetActive) {
        this.send('segmentationFilterClicked', id, strResult);
      }
    }
  }

  /**
   * Close the modal to select visible segmentation blocks and sort them
   */
  @action closeSelectionAndSortModal(): void {
    this.isShowingSelectionAndSortModal = false;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.checkForMissingBlocks(this.segmentationFilters);
  }

  @action exportSegmentDataMulti(): void {
    this.usageAnalytics.track('Data Exported', {
      eventTrigger: 'general-export',
      exportType: 'general',
      days: this.toDateMoment.diff(this.fromDateMoment, 'days'),
    });

    this.exportSegmentData();
  }

  @action filterBarSegmentationClicked(
    segmentationId: RiBreakdown,
    segmentationValueId: string,
  ): void {
    if (segmentationId === RI_BREAKDOWNS.IMPACT_DATE) {
      this.send('stayDateFilterClicked', segmentationId, segmentationValueId);
    } else {
      this.send(
        'segmentationFilterClicked',
        segmentationId,
        segmentationValueId,
      );
    }
  }

  /**
   * Open the modal to select visible segmentation blocks and sort them
   */
  @action openSelectionAndSortModal(): void {
    this.isShowingSelectionAndSortModal = true;
  }

  @action rangeChanged(range: { end: Dayjs | null; start: Dayjs }): void {
    // Reset loading state
    this.showLoadingPlaceholderState = true;
    this.setStayDateFilter('');
    if (range.start) {
      this.fromDate = range.start.format('YYYY-MM-DD');
    }
    if (range.end) {
      this.toDate = range.end.format('YYYY-MM-DD');
    } else {
      this.toDate = range.start.format('YYYY-MM-DD');
    }

    if (this.reference === 'SWLY') {
      this.refFromDate = range.start
        .subtract(WEEKS_IN_YEAR, 'week')
        .format('YYYY-MM-DD');
    } else if (this.reference === 'SDLY') {
      this.refFromDate = range.start.subtract(1, 'year').format('YYYY-MM-DD');
    } else if (this.reference === 'PP') {
      this.refFromDate = this.calculatePPstart(range.start).format(
        'YYYY-MM-DD',
      );
    }

    // Custom logic: if selected date ranges are changed (not custom, ref dates), we need to drop active pickup & pace filters
    if (this.parsedSegmentationFilters) {
      let didUpdateFilters = false;

      if (this.parsedSegmentationFilters['pace']) {
        delete this.parsedSegmentationFilters['pace'];
        didUpdateFilters = true;
      }

      if (this.parsedSegmentationFilters['pickup']) {
        delete this.parsedSegmentationFilters['pickup'];
        didUpdateFilters = true;
      }

      if (didUpdateFilters) {
        if (Object.keys(this.parsedSegmentationFilters).length) {
          this.setSegmentationFilters(
            JSON.stringify(this.parsedSegmentationFilters),
          );
        } else {
          this.setSegmentationFilters('');
        }
      }
    }

    // Check if we need to set a new staydate aggregation
    const { start, end } = this.range;

    const rangeInDays = end.diff(start, 'day');
    // Default aggregation is daily
    let rangeAggregationPeriod: DateAggregationType = 'day';
    // Switch to monthly if the new range is over 92 days
    if (rangeInDays > 92) {
      // 92 days is +- > 3 months (a quarter)
      rangeAggregationPeriod = 'month';
    }
    this.stayDateAggregation = rangeAggregationPeriod;
  }

  @action redirectOnClose(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo('market.revenue-insight');
  }

  @action refRangeChanged(range: { start: Dayjs }, option: string): void {
    // Reset loading state
    this.showLoadingPlaceholderState = true;
    this.reference = option;

    if (range.start) {
      this.refFromDate = range.start.format('YYYY-MM-DD');
    }
  }

  @action refreshRoute(): void {
    this.send('doRefresh');
  }

  @action segmentationFilterClicked(
    segmentationId: RiBreakdown,
    segmentationValueId: string,
  ): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.segmentationFilterClickedTask.perform(
      segmentationId,
      segmentationValueId,
    );
  }

  // Used in filter bar for setting favourites
  @action setFilterValues(updatedFilterValues: {
    segmentationFilters: string;
    stayDateFilter: string;
  }): void {
    this.setSegmentationFilters(updatedFilterValues.segmentationFilters);
    this.setStayDateFilter(updatedFilterValues.stayDateFilter);
  }

  @action setHidePickupLY(value: boolean): void {
    this.hidePickupLY = value;
  }

  @action setIsFilterBarHovered(isFilterBarHovered: boolean): void {
    this.isFilterBarHovered = isFilterBarHovered;
  }

  @action setIsSearchActive(
    segmentationId: FilterableSegmentationBlock,
    isSearchActive: boolean,
  ): void {
    const { searchValuesPerSegmentationId } = this;
    set(
      searchValuesPerSegmentationId[segmentationId],
      'isSearchActive',
      isSearchActive,
    );
  }

  @action setSearchValue(
    segmentationId: FilterableSegmentationBlock,
    searchValue: string,
  ): void {
    const { searchValuesPerSegmentationId } = this;
    set(
      searchValuesPerSegmentationId[segmentationId],
      'searchValue',
      searchValue,
    );
  }

  @action setSortHotelsAscending(ascending: boolean): void {
    this.sortHotelsAscending = ascending;
  }

  @action setSortHotelsBy(sortBy: HotelSortBy): void {
    this.sortHotelsBy = sortBy;
  }

  @action stayDateAggregationKeyChanged(aggregationChoice: Option): void {
    this.usageAnalytics.track('Stay Date View Changed', {
      eventTrigger: 'stay-date-chart-view',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      viewBy: aggregationChoice['id'],
    });

    this.stayDateAggregation = aggregationChoice['id'] as DateAggregationType;
  }

  @action stayDateFilterClicked(
    segmentationId: RiBreakdown,
    segmentationValueId: string,
  ): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const currentFilters = parseObject(this.stayDateFilter);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if ((currentFilters?.[segmentationId] ?? [])[0] === segmentationValueId) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      delete currentFilters[segmentationId];
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      currentFilters[segmentationId] = [segmentationValueId];
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const stringifiedFilters = stringifyObject(currentFilters);
    this.setStayDateFilter(stringifiedFilters);
  }

  @action toggleFullPortfolio(): void {
    this.showLoadingPlaceholderState = true;
    this.showFullPortfolio = !this.showFullPortfolio;

    this.usageAnalytics.track('Hotel Included Changed', {
      eventTrigger: 'full-portfolio-toggle',
      showFullPortfolio: this.showFullPortfolio,
    });
  }

  @action updateDisplayedSegmentationFilters(): void {
    // create object with all filters. Will not be in correct order
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const filters = mergeFilters(this.segmentationFilters, this.stayDateFilter);

    // put in correct order
    const segmentationValueFormatterParams = {
      intl: this.intl,
      isFuture: this.riSelections.isToDateInFuture,
      segmentationDisplayNameOverride: this.filterDisplayNameOverride,
    };
    this.activeFilters = addFiltersToActiveFilters(
      this.activeFilters,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      filters,
      segmentationIdFormatter,
      segmentationValueFormatter,
      segmentationValueFormatterParams,
      this.riSelections,
    );
  }

  // Only show the loading placeholder on the initial load
  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateShowLoadingPlaceholderState() {
    if (this.isInitialVisit) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.checkForMissingBlocks(this.segmentationFilters, true);
      this.isInitialVisit = false;
    }

    if (this.isModelLoading) {
      this.showLoadingPlaceholderState = true;
    } else if (
      this.showLoadingPlaceholderState &&
      this.isModelLoading === false
    ) {
      this.showLoadingPlaceholderState = false;
      return true;
    }

    return false;
  }

  @action viewPortfolioCurrencies(): void {
    this.usageAnalytics.track('Portfolio Currencies Viewed', {
      eventTrigger: 'currency-picker',
    });

    this.router.transitionTo('market.revenue-insight.currency-table');
  }
}
